import type { useIntlayer } from 'react-intlayer'
import type { SpawnMode, WorkItemHistoryEntry, WorkItemStatus } from '../domain/workflow'

// Pure label mappers shared by the detail sheet and the work-item page. They
// take the intlayer content node `c` (the `work-item.detail` block) and a
// member-name resolver, so the display strings stay identical across both views.
type DetailContent = ReturnType<typeof useIntlayer<'work-item'>>['detail']

const statusLabels = (c: DetailContent): Record<WorkItemStatus, string> => ({
	inbox: c.statusInbox.value,
	open: c.statusOpen.value,
	awaiting_handoff: c.statusAwaitingHandoff.value,
	completed: c.statusCompleted.value,
	cancelled: c.statusCancelled.value,
	archived: c.statusArchived.value,
})

const modeLabel = (c: DetailContent, mode: SpawnMode | null): string =>
	mode === 'blocking' ? c.modeBlocking.value : mode === 'async' ? c.modeAsync.value : '-'

const historyLabel = (c: DetailContent, entry: WorkItemHistoryEntry): string => {
	switch (entry.action) {
		case 'work_item.created':
			return c.historyCreated.value
		case 'work_item.updated':
			return c.historyUpdated.value
		case 'work_item.assigned':
			return c.historyAssigned.value
		case 'work_item.reassigned_member':
			return c.historyReassignedMember.value
		case 'work_item.reassigned_orphaned':
			return c.historyReassignedOrphaned.value
		case 'work_item.cancelled':
			return c.historyCancelled.value
		case 'work_item.completed':
			return c.historyCompleted.value
		case 'work_item.reopened':
			return c.historyReopened.value
		case 'work_item.work_state_changed':
			return c.historyWorkStateChanged.value
		case 'work_item.archived':
			return c.historyArchived.value
		case 'work_item.reassigned':
			return c.historyReassigned.value
		case 'step.transitioned':
			return c.historyTransitioned.value
		case 'phase.handed_off':
			return c.historyHandedOff.value
		case 'phase.accepted':
			return c.historyAccepted.value
		case 'phase.rejected':
			return c.historyRejected.value
		case 'comment.added':
			return c.historyCommentAdded.value
		case 'comment.edited':
			return c.historyCommentEdited.value
		case 'spawn.created':
			return c.historySpawnCreated.value
		case 'spawn.status_changed':
			return c.historySpawnStatusChanged.value
		case 'spawn.mode_changed':
			return c.historySpawnModeChanged.value
		case 'spawn.lightweight_changed':
			return c.historySpawnLightweightChanged.value
		case 'spawn.archived':
			return c.historySpawnArchived.value
	}
}

const historyDetail = (
	c: DetailContent,
	memberName: (id: string | null) => string,
	entry: WorkItemHistoryEntry,
): string | null => {
	switch (entry.action) {
		case 'work_item.assigned':
			return memberName(entry.payload.assignedTo)
		case 'work_item.reassigned_member':
			return `${memberName(entry.payload.from)} → ${memberName(entry.payload.to)}`
		case 'work_item.reassigned_orphaned':
			return `${memberName(entry.payload.from)} → ${entry.payload.to ? memberName(entry.payload.to) : c.statusInbox.value}`
		case 'work_item.cancelled':
		case 'phase.rejected':
			return entry.payload.reason
		case 'work_item.updated':
			return entry.payload.fields.join(', ')
		case 'step.transitioned':
		case 'phase.handed_off':
			return entry.payload.label
		case 'spawn.created':
			return `${entry.payload.title} (${modeLabel(c, entry.payload.mode)})`
		case 'spawn.status_changed':
			return `${entry.payload.from} → ${entry.payload.to}`
		case 'spawn.mode_changed':
			return `${modeLabel(c, entry.payload.from)} → ${modeLabel(c, entry.payload.to)}`
		case 'work_item.archived':
			return entry.payload.cascadedSpawnCount
				? `${entry.payload.cascadedSpawnCount} ${c.historyCascaded.value}`
				: null
		default:
			return null
	}
}

export type { DetailContent }
export { statusLabels, modeLabel, historyLabel, historyDetail }
